import { ArrowDown, ArrowRight, ArrowUp, Check } from 'lucide-react'
import { useId } from 'react'
import type { LadderRung, MoveId } from '@/lib/audition/level-ladder'
import {
  ladderRungs,
  MOVE_IDS,
  movesReadback,
} from '@/lib/audition/level-ladder'
import { cn } from '@/lib/utils'

const GLYPHS: Record<MoveId, typeof ArrowUp> = {
  up: ArrowUp,
  lateral: ArrowRight,
  down: ArrowDown,
}

/**
 * "Given a compelling offer, what levels would you actually accept?" — up on
 * top, sideways in the middle, down at the bottom, on a drawn upright.
 *
 * The three moves are a set, not one choice: plenty of people would take a
 * step up OR a lateral, and saying so is the answer. Each rung names the
 * applicant's real neighbouring levels (see `ladderRungs`), so the question
 * is "how far from where you actually are" rather than an abstract scale.
 *
 * A multi-select group of toggle buttons rather than a radiogroup, matching
 * ChipGroup's multi branch: a checkbox-style group has no roving-focus
 * convention to honor, so every rung is its own tab stop.
 */
export function LevelLadder({
  label,
  currentLevel,
  value,
  onChange,
  disabled,
  describedBy,
  invalid,
}: {
  label: string
  /** Their answer to the current-level chips, which names the rungs. */
  currentLevel: string | undefined
  value: string[]
  onChange: (value: string[]) => void
  disabled?: boolean
  /** Id of help text describing this group, hung on the group itself. */
  describedBy?: string
  /** Whether this group is why the step was refused. */
  invalid?: boolean
}) {
  const rungs = ladderRungs(currentLevel)
  // Scoped per instance: the sub-label ids used to be keyed on the move
  // alone, so a second ladder anywhere on the page would duplicate them and
  // point aria-describedby at the wrong explanation.
  const uid = useId()

  // Rebuilt from MOVE_IDS rather than appended, so the stored array always
  // reads in ladder order — otherwise untap/retap silently reorders the
  // answer and two identical applications compare unequal.
  const toggle = (id: MoveId) =>
    onChange(
      MOVE_IDS.filter((m) =>
        m === id ? !value.includes(m) : value.includes(m),
      ),
    )

  return (
    <div className="space-y-3">
      <div
        role="group"
        aria-label={label}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        className="relative space-y-2"
      >
        {/* The ladder's upright, behind the glyph column. Decorative: the
            rungs already say which direction each move goes. */}
        <span
          aria-hidden="true"
          className="absolute left-[29px] top-3 bottom-3 w-0.5 bg-border"
        />
        {rungs.map((rung) => (
          <Rung
            key={rung.id}
            uid={uid}
            rung={rung}
            on={value.includes(rung.id)}
            disabled={!!disabled}
            onToggle={() => toggle(rung.id)}
          />
        ))}
      </div>
      {/* The plain-English readback. A widget without one is a puzzle. */}
      <p aria-live="polite" className="text-sm text-muted-foreground">
        {movesReadback(value)}
      </p>
    </div>
  )
}

function Rung({
  uid,
  rung,
  on,
  disabled,
  onToggle,
}: {
  uid: string
  rung: LadderRung
  on: boolean
  disabled: boolean
  onToggle: () => void
}) {
  const Glyph = GLYPHS[rung.id]
  const subId = `${uid}-${rung.id}-sub`
  const hereId = `${uid}-${rung.id}-here`

  return (
    <button
      type="button"
      // The accessible name is the move itself; the sub-label names levels,
      // which belongs in the description rather than in the name a screen
      // reader announces on every focus.
      aria-label={rung.label}
      // The "you are here" mark is part of the answer, not decoration: it is
      // the only thing saying which rung the applicant is standing on. An
      // aria-label replaces the whole subtree as the accessible name, so
      // without this the mark is visible and never announced.
      aria-describedby={rung.isHere ? `${hereId} ${subId}` : subId}
      aria-pressed={on}
      // A closed rung stays focusable. `disabled` would drop it out of the
      // tab order, and its sub-label — the explanation for why it cannot be
      // picked — is then something a screen-reader user can never reach.
      // Only a save in flight actually disables the control.
      aria-disabled={rung.closed || undefined}
      disabled={disabled}
      onClick={rung.closed ? undefined : onToggle}
      className={cn(
        'relative z-1 flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-colors',
        'outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px]',
        // Closed rungs stay legible: the sub-label is the explanation for
        // why they cannot be picked, so fading it out defeats the point.
        'disabled:pointer-events-none disabled:opacity-60',
        rung.closed && 'opacity-60',
        on
          ? 'border-primary bg-accent shadow-sm'
          : 'border-input bg-background hover:bg-accent/50',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-full border',
          on
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-input bg-background text-muted-foreground',
        )}
      >
        <Glyph className="size-4" />
      </span>

      <span className="min-w-0 flex-1 space-y-0.5">
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{rung.label}</span>
          {rung.isHere && (
            <span
              id={hereId}
              className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-px text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              You are here
            </span>
          )}
        </span>
        <span
          id={subId}
          className="block text-sm text-pretty text-muted-foreground"
        >
          {rung.sub}
        </span>
      </span>

      <span
        aria-hidden="true"
        className={cn(
          'flex size-5.5 shrink-0 items-center justify-center rounded-md border',
          on
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-input',
        )}
      >
        {on && <Check className="size-3.5" />}
      </span>
    </button>
  )
}
